import { useState, useContext } from 'react'
import NextImage from 'next/image'

import { HasReviewsStrings } from '@data/sanity/queries/types/site'
import { getBasicImageLoader } from '@lib/image'
import { ProductContext } from '@lib/product-context'
import { Reviews } from '@lib/review'
import { StringsContext } from '@lib/strings'

import Button from '@components/buttons/button'
import Stars from '@components/stars'
import ReviewForm from './review-form'

interface ReviewListProps extends HasReviewsStrings {
  reviews: Reviews
  paginationLimit?: number
}

const ReviewList = ({
  reviews,
  paginationLimit = 5,
  reviewsStrings,
}: ReviewListProps) => {
  const strings = useContext(StringsContext)
  const { product } = useContext(ProductContext)

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [pagination, setPagination] = useState([
    ...reviews.items.slice(0, paginationLimit),
  ])
  const [hasMore, setHasMore] = useState(
    reviews.items && reviews.items.length > paginationLimit
  )

  const hasPagination = paginationLimit > 0
  const reviewsList = hasPagination ? pagination : reviews.items

  const loadMore = () => {
    const curPage = pagination.length
    const nextPage = reviews.items.slice(curPage, curPage + paginationLimit)
    const newPage = [...pagination, ...nextPage]

    if (hasMore) {
      setPagination(newPage)
      setHasMore(newPage.length < reviews.items.length)
    }
  }

  const toggleForm = () => {
    setIsFormOpen(!isFormOpen)
  }

  return (
    <div className="container">
      <div className="flex items-center justify-between py-4 mb-6">
        <div className="flex items-center flex-wrap">
          <div className="flex items-center flex-nowrap mr-3">
            <p className="text-3xl leading-snug font-semibold mr-3">
              {reviews.rating.toFixed(1)}
            </p>
            <Stars ratingValue={reviews.rating} readonly size={24} />
          </div>
          <p className="mt-[2px]">
            {reviews.items.length === 1
              ? reviewsStrings.reviewsRatingBasedOnSingular?.replace(
                  /{count}/gi,
                  reviews.items.length.toString()
                )
              : reviewsStrings.reviewsRatingBasedOnPlural?.replace(
                  /{count}/gi,
                  reviews.items.length.toString()
                )}
          </p>
        </div>
        {product && (
          <div>
            <Button secondary onClick={toggleForm}>
              {reviewsStrings.reviewsWriteReview}
            </Button>
          </div>
        )}
      </div>

      {isFormOpen && product && (
        <ReviewForm
          product={product}
          className="mb-14"
          onCancelClick={toggleForm}
          reviewsStrings={reviewsStrings}
        />
      )}

      <div>
        {reviewsList.map((review, i) => (
          <div key={i} className="flex mb-8">
            <div className="min-w-[55px] h-[55px] relative rounded-full overflow-hidden mr-4">
              {review.avatar && (
                <NextImage
                  src={
                    review.avatar.startsWith('//')
                      ? `https:${review.avatar}`
                      : review.avatar
                  }
                  loader={getBasicImageLoader}
                  fill
                  unoptimized
                  alt={review.author}
                  className="object-cover"
                />
              )}
            </div>
            <div className="w-full">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm">
                    <span className="font-semibold mr-2">{review.author}</span>
                    {review.isVerified && reviewsStrings.reviewsVerifiedBuyer}
                  </p>
                  <p className="text-xs opacity-50 mb-1">{review.country}</p>
                  <Stars
                    ratingValue={review.rating}
                    readonly
                    size={15}
                    className="mb-4"
                  />
                </div>
                <p className="opacity-50 text-sm">{review.date}</p>
              </div>

              <p className="font-semibold">{review.title}</p>
              <p className="text-sm mb-2">{review.message}</p>
              <p className="text-sm opacity-60">
                {review.productName}{' '}
                {review.productVariantName && `- ${review.productVariantName}`}
              </p>
            </div>
          </div>
        ))}

        {hasPagination && hasMore && (
          <div className="py-4 sm:py-8 text-center">
            {hasMore && (
              <Button primary onClick={loadMore}>
                {strings.buttonLoadMore}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ReviewList
